import autobind from 'class-autobind';

import OwnReduction from 'atg/api/OwnReduction';
import OwnEvaluationDialog from 'atg/components/dialog/OwnEvaluationDialog';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import { formatCalendar } from 'shared/utils/dateUtils';
import * as Text from 'shared/utils/Text';

import {
  HEAVILY_OVERPLAYED,
  HEAVILY_UNDERPLAYED,
  KELLYLOOKUPTABLE,
  NEUTRAL,
  OVERPLAYED,
  UNDERPLAYED,
} from '../../constants/KellyState';
import { createHelpIcon } from '../HelpIcon';
import BaseView from './BaseView';

const $ = window.$;

export default class OwnReductionView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, OwnReductionView.prototype);
  }

  getGridClass() {
    return 'view-own-reduction';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get('view-own-reduction', this.getNumber(OwnReductionView));
  }

  getMenuTitle() {
    return Text.get('view-own-reduction', this.getNumber(OwnReductionView));
  }

  getMenuGroupTitle() {
    return Text.get('view-group-points');
  }

  getMenuAddItemTitle() {
    return Text.get('view-points-add');
  }

  getMenuMaxNumberOfViews() {
    return this.getOwnPercentReductionSet().getMaxNumberOfPointReductions();
  }

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  // ---------------------------------------- TOOLBAR ----------------------------------------

  hasToolbar() {
    return true;
  }

  // ---------------------------------------- GRID ----------------------------------------
  onRowWasUpdated() {}

  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let betConfig;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      betConfig = rowCache.betConfig;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};
      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;

    const start = race.getStart(startIndex);

    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    if (betRow) {
      let rowPercent = betRow.getOwnReductionPercentage(position.column);

      if (rowPercent == null) {
        rowPercent = 0;
        betRow.setOwnReductionPercentage(position.column, rowPercent);
      }

      rowPercent =
        rowPercent > 10
          ? Utils.compactPercent(rowPercent, false)
          : rowPercent.toFixed(1);

      const fragment = $('<div/>').addClass('formel-container');

      const currentSpan = $('<span/>').addClass('own-profit-percent');
      const trendSpan = $('<span/>').addClass('formel-marker');

      currentSpan.text(rowPercent);

      const stake = this.getAPI().getStake();
      const kellyState = this.lookupKellys(
        stake.getOwnWinPercentageDistribution()
          ? stake.getOwnWinPercentageDistribution()[position.row][startIndex]
          : gameRow.getMarkPercent(startIndex),
        betRow.getOwnReductionPercentage(position.column),
      );

      this.setKellyOnGrid(kellyState, trendSpan);

      fragment.append(currentSpan, trendSpan);
      info.bottomBlue = fragment;
    }

    return info;
  }

  setKellyOnGrid(kellyState, element) {
    switch (kellyState) {
      case HEAVILY_OVERPLAYED:
        element.toggleClass('highly-overvalued');

        break;

      case OVERPLAYED:
        element.toggleClass('overvalued');

        break;

      case UNDERPLAYED:
        element.toggleClass('undervalued');

        break;

      case HEAVILY_UNDERPLAYED:
        element.toggleClass('highly-undervalued');

        break;

      default:
      // Do nothing
    }
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
      {
        icon: 'percent-sign',
        onPress: this.onViewPercentageSinglePressed,
      },
      ...this.makeHorseButtons(position),
    ];

    this.getGrid().showCellButtons(position, buttons);
  }

  onViewPercentageSinglePressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();
    const grid = this.getGrid();
    grid.closeHighlight();

    if (!willShowLockedAlert) {
      this.evaluationDialog = new OwnEvaluationDialog(this, grid);
      this.evaluationDialog.showOwnEvaluationDialog(position);
    }
  }

  ownProfitPercentageChange(value, position) {
    const betRow = this.getAPI().betInformation.getRow(position.row);
    betRow.setOwnReductionPercentage(position.column, value);
    // this.onGridNumpadButtonPressed(position);
    this.getGrid().updateRow(position.row);
    this.getAPI().scheduleBetUpdate();
  }

  canChangeView() {
    const isReductionEmpty = this.getOwnPercentReduction().isEmpty();
    if (isReductionEmpty) {
      return super.canChangeView();
    }

    const invalidLegs = this.getPercentages()
      .filter(el => el.percentage !== 100)
      .map(el => el.title);

    if (invalidLegs.length > 0) {
      const reason = Text.get(
        'view-own-reduction-no-view-change',
        invalidLegs.join(', '),
      );
      return {
        ok: false,
        reason: {
          title: Text.get('view-own-reduction-no-view-heading'),
          details: reason,
        },
      };
    }
    return super.canChangeView();
  }
  // ---------------------------------------- DATA ----------------------------------------

  getPercentageLeft(position) {
    const percentages = [];
    const betRow = this.getAPI().betInformation.getRow(position.row);
    const columns = betRow.getOwnReductionPercentages().length;
    let result = 0;

    for (let index = 0; index < columns; index++) {
      const percent = betRow.getOwnReductionPercentage(index).toFixed(1);
      percentages.push(Number.parseFloat(percent));
    }

    result =
      100 -
      percentages.reduce((item, currentItem) => {
        if (currentItem == null) {
          return item;
        }
        return item + currentItem;
      });
    result = Math.round(result);
    return Utils.compactPercent(result);
  }

  resetOwnWinPercentages() {
    const gridSize = this.getGrid().getSize();
    for (let row = 0; row < gridSize.rows; row++) {
      const betRow = this.getAPI().betInformation.getRow(row);
      for (let column = 0; column < betRow.getOrder().length; column++) {
        betRow.setOwnReductionPercentage(column, 0);
      }
    }
    this.getGrid().updateAll();
    this.getAPI().scheduleBetUpdate();
  }

  getIndex() {
    return super.getIndex(OwnReductionView);
  }

  getOwnPercentReduction() {
    const reductionSet = this.getOwnPercentReductionSet();
    let reduction;

    if (reductionSet) {
      const index = this.getIndex();
      const numberOfReductions = reductionSet.getNumberOfOwnProfitReductions();

      if (index < numberOfReductions) {
        reduction = reductionSet.getOwnProfitReduction(index);
      }
    }

    if (!reduction) {
      reduction = new OwnReduction();
    }

    return reduction;
  }

  getOwnPercentReductionSet() {
    return this.getAPI().betInformation.getReductionSet().getOwnReduction();
  }

  getItem() {
    return this.getOwnPercentReduction();
  }

  addItem() {
    return this.getOwnPercentReductionSet().addPointReduction();
  }

  deleteItem() {
    const api = this.getAPI();
    const index = this.getIndex();

    this.getOwnPercentReductionSet().deleteOwnProfitReduction(index);

    const numberOfRows = api.betInformation.getNumberOfRows();
    for (let row = 0; row < numberOfRows; row++) {
      api.betInformation.getRow(row).deleteOwnReductionPercentage();
    }

    this.getAPI().scheduleBetUpdate();
  }

  getNumberOfItems() {
    const set = this.getOwnPercentReductionSet();
    return set ? set.getNumberOfPointReductions() : 0;
  }

  lookupKellys(betDistributionPercentage, ownPercentage) {
    const roundedDistribution =
      betDistributionPercentage > 10
        ? Math.round(betDistributionPercentage)
        : Math.round(betDistributionPercentage * 10) / 10;
    for (const element of KELLYLOOKUPTABLE) {
      if (roundedDistribution < element[0]) {
        if (roundedDistribution - ownPercentage >= element[4]) {
          return HEAVILY_OVERPLAYED;
        } else if (roundedDistribution - ownPercentage >= element[3]) {
          return OVERPLAYED;
        } else if (ownPercentage - roundedDistribution >= element[2]) {
          return HEAVILY_UNDERPLAYED;
        } else if (ownPercentage - roundedDistribution >= element[1]) {
          return UNDERPLAYED;
        }
        return NEUTRAL;
      }
    }
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoHidden() {
    return false;
  }

  getBottomInfoContent() {
    const distributionTimestamp = this.getAPI()
      .getStake()
      .getOwnWinPercentageDistributionTimestamp();

    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-own-profit-percent',
      {
        /*** Section 1 ***/
        '%bet-distribution-header%': Text.get(
          'bottom-conditions-own-profit-betdistribution-header',
        ),
        /*** Section 2 ***/
        '%overplayed-horses-header%': Text.get(
          'bottom-conditions-own-profit-overplayed-header',
        ),
        /*** Section 3 ***/
        '%underplayed-horses-header%': Text.get(
          'bottom-conditions-own-profit-underplayed-header',
        ),
        /*** Section 4 ***/
        '%underplayed-must-win-header%': Text.get(
          'bottom-conditions-own-profit-underplayed-win-header',
        ),
        /*** Section 5 ***/
        '%overplayed-must-win-header%': Text.get(
          'bottom-conditions-own-profit-overplayed-win-header',
        ),
        '%button-update%': Text.get('button-update'),
        '%distribution-updated-at%':
          distributionTimestamp != null
            ? Text.get(
                'bottom-conditions-dividend-updated-at',
                formatCalendar(distributionTimestamp.getTime(), {
                  showTimeForToday: true,
                }),
              )
            : '',
      },
    ));

    $conditions.find('.bet-distribution h2').append(
      createHelpIcon({
        header: Text.get('help-own-evaluation-gamevalue-header'),
        body: Text.get('help-own-evaluation-gamevalue-body'),
      }),
    );

    $conditions.find('.overplayed-horses h2').append(
      createHelpIcon({
        header: Text.get('help-own-evaluation-overplayed-header'),
        body: Text.get('help-own-evaluation-overplayed-body'),
      }),
    );

    $conditions.find('.underplayed-horses h2').append(
      createHelpIcon({
        header: Text.get('help-own-evaluation-underplayed-header'),
        body: Text.get('help-own-evaluation-underplayed-body'),
      }),
    );

    $conditions.find('.underplayed-must-win h2').append(
      createHelpIcon({
        header: Text.get('help-own-evaluation-strong-underplayed-header'),
        body: Text.get('help-own-evaluation-strong-underplayed-body'),
      }),
    );

    $conditions.find('.overplayed-must-win h2').append(
      createHelpIcon({
        header: Text.get('help-own-evaluation-strong-overplayed-header'),
        body: Text.get('help-own-evaluation-strong-overplayed-body'),
      }),
    );

    this.updateOwnPercentageReduction();
    this.updateConditionsBetDistribution();
    this.updateOverplayedHorses();
    this.updateUnderplayedHorses();
    this.updateUnderplayedHorsesMustWin();
    this.updateOverplayedHorsesMustWin();

    return $conditions;
  }

  // ---------------------------------------- BET DISTRIBUTION ----------------------------------------

  updateOwnPercentageReduction() {
    const latestTurnoverAt = this.getAPI().gameInformation.getTimestamp();
    const distributionTimestamp = this.getAPI()
      .getStake()
      .getOwnWinPercentageDistributionTimestamp();

    if (
      latestTurnoverAt != null &&
      distributionTimestamp != null &&
      distributionTimestamp.getTime() < latestTurnoverAt.getTime()
    ) {
      this.$conditions
        .find('.ownwinpercentage-update-wrapper button')
        .reevent('click', () => {
          Utils.callDelegate(this.delegate, 'updateWinPercentageCalculation');
        });
    } else {
      this.$conditions.find('.ownwinpercentage-update-wrapper').hide();
    }
  }

  updateConditionsBetDistribution() {
    const reduction = this.getOwnPercentReduction();

    const $wrapper = this.$conditions.find('.bet-distribution');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const minPlayworthyRow = reduction.getMinPlayworthyRow();

    const $row = StickyTemplate.jq('bottom-conditions-bet-distribution-row', {
      '%play-only%': Text.get('bottom-conditions-own-profit-play-only'),
      '%bet-distribution%': Text.get(
        'bottom-conditions-own-profit-betdistribution',
      ),
      '%row%': 0,
    });

    $row
      .find('.bet-distribution-value')
      .val(Utils.negativeBlankToValue(minPlayworthyRow));

    $rows.append($row);

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    this.attachNegativeBlankEvents($wrapper);
  }

  // ---------------------------------------- OVER PLAYED ----------------------------------------

  updateOverplayedHorses() {
    const reduction = this.getOwnPercentReduction();

    const $wrapper = this.$conditions.find('.overplayed-horses');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const minOverplayed = reduction.getMinOverplayedHorses();
    const maxOverplayed = reduction.getMaxOverplayedHorses();

    const $row = StickyTemplate.jq('bottom-conditions-overplayed-horses-row', {
      '%Min%': Text.get('bottom-conditions-Min'),
      '%max%': `, ${Text.get('bottom-conditions-max')}`,
      '%overplayed-horses%': Text.get(
        'bottom-conditions-own-profit-play-overplayed',
      ),
      '%row%': 0,
    });

    this.addOwnEvaluationInput($row);

    $row.find('.from-points').val(Utils.negativeBlankToValue(minOverplayed));
    $row.find('.to-points').val(Utils.negativeBlankToValue(maxOverplayed));

    $rows.append($row);

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    this.attachNegativeBlankEvents($wrapper);
  }

  // ---------------------------------------- UNDER PLAYED ----------------------------------------

  updateUnderplayedHorses() {
    const reduction = this.getOwnPercentReduction();

    const $wrapper = this.$conditions.find('.underplayed-horses');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const minUnderplayed = reduction.getMinUnderplayedHorses();
    const maxUnderplayed = reduction.getMaxUnderplayedHorses();

    const $row = StickyTemplate.jq('bottom-conditions-underplayed-horses-row', {
      '%Min%': Text.get('bottom-conditions-Min'),
      '%max%': `, ${Text.get('bottom-conditions-max')}`,
      '%underplayed-horses%': Text.get(
        'bottom-conditions-own-profit-play-underplayed',
      ),
      '%row%': 0,
    });

    this.addOwnEvaluationInput($row);

    $row.find('.from-points').val(Utils.negativeBlankToValue(minUnderplayed));
    $row.find('.to-points').val(Utils.negativeBlankToValue(maxUnderplayed));

    $rows.append($row);

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    this.attachNegativeBlankEvents($wrapper);
  }

  // ---------------------------------------- OVER PLAYED MUST WIN ----------------------------------------
  updateUnderplayedHorsesMustWin() {
    const reduction = this.getOwnPercentReduction();

    const $wrapper = this.$conditions.find('.underplayed-must-win');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const minMostUnderplayed = reduction.getMinMostUnderplayedHorses();

    const $row = StickyTemplate.jq(
      'bottom-conditions-underplayed-must-win-row',
      {
        '%Min%': Text.get('bottom-conditions-Min'),
        '%must-win%': Text.get(
          'bottom-conditions-own-profit-play-most-underplayed',
        ),
        '%row%': 0,
      },
    );

    this.addOwnEvaluationInput($row);

    $row
      .find('.min-underplayed')
      .val(Utils.negativeBlankToValue(minMostUnderplayed));

    $rows.append($row);

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    this.attachNegativeBlankEvents($wrapper);
  }

  // ---------------------------------------- UNDER PLAYED MUST WIN ----------------------------------------

  updateOverplayedHorsesMustWin() {
    const reduction = this.getOwnPercentReduction();

    const $wrapper = this.$conditions.find('.overplayed-must-win');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const maxOverPlayed = reduction.getMaxMostOverplayedHorses();

    const $row = StickyTemplate.jq(
      'bottom-conditions-overplayed-must-win-row',
      {
        '%max%': Text.get('bottom-conditions-max-cap'),
        '%should-win%': Text.get(
          'bottom-conditions-own-profit-play-most-overplayed',
        ),
        '%row%': 0,
      },
    );

    this.addOwnEvaluationInput($row);

    $row.find('.max-overplayed').val(Utils.negativeBlankToValue(maxOverPlayed));

    $rows.append($row);

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    this.attachNegativeBlankEvents($wrapper);
  }

  // ---------------------------------------- FORM TO DATA ----------------------------------------

  getReductionFormData(formData, reduction) {
    reduction.setMinPlayworthyRow(
      Utils.valueToNegativeBlank(formData.minPlayworthyRow),
    );
    reduction.setMinOverplayedHorses(
      Utils.valueToNegativeBlank(formData.minOverplayedHorses),
    );
    reduction.setMaxOverplayedHorses(
      Utils.valueToNegativeBlank(formData.maxOverplayedHorses),
    );
    reduction.setMinUnderplayedHorses(
      Utils.valueToNegativeBlank(formData.minUnderplayedHorses),
    );
    reduction.setMaxUnderplayedHorses(
      Utils.valueToNegativeBlank(formData.maxUnderplayedHorses),
    );
    reduction.setMinMostUnderplayedHorses(
      Utils.valueToNegativeBlank(formData.minMostUnderplayedHorses),
    );
    reduction.setMaxMostOverplayedHorses(
      Utils.valueToNegativeBlank(formData.maxMostOverplayedHorses),
    );
  }

  onConditionsChanged() {
    const $form = this.$conditions.find('form');
    const formData = $form.formData();
    const reduction = this.getOwnPercentReduction();

    this.getReductionFormData(formData, reduction);

    this.getAPI().scheduleBetUpdate();
  }

  onGridNumberInputChanged(position, value) {
    const grid = this.getGrid();
    const betRow = this.getAPI().betInformation.getRow(position.row);
    const points =
      value > 10
        ? Number.parseFloat(value).toFixed(0)
        : Number.parseFloat(value).toFixed(2);

    betRow.setOwnReductionPercentage(position.column, points);

    const percentRemaining = $('.cell-buttons .remaining');
    percentRemaining.text(this.getPercentageLeft(position));

    grid.updateRow(position.row);
    this.getAPI().scheduleBetUpdate();
  }

  getPercentages() {
    const gridSize = this.getGrid().getSize();
    const percentages = [];
    for (let row = 0; row < gridSize.rows; row++) {
      const rowInfo = this.getGridRowInfo(row);
      const betRow = this.getAPI().betInformation.getRow(row);
      let percentageSum = 0;
      for (let column = 0; column < gridSize.columns; column++) {
        const ownPercentage = betRow.getOwnReductionPercentage(column);
        if (!ownPercentage) {
          continue;
        }
        percentageSum = percentageSum + ownPercentage;
      }
      percentages.push({
        title: rowInfo.title,
        percentage: percentageSum,
      });
    }

    return percentages;
  }
}
